import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import profilePic from '../assests/profile.jpg';
import logo from '../assests/logo.svg';
import logoMark from '../assests/logo-mark.svg';
import { MessageCircle, Send, Mail, Menu, X, Globe, Check, Star } from 'lucide-react';
import usePriceEquivalents from './usePriceEquivalents';
import usePrice from './usePrice';

/* Фиксированная цена курса в обеих валютах: по курсу не пересчитывается. */
const COURSE_USD = 5.9;

/* ── Contacts ─────────────────────────────────────────────────── */
const CONTACT = {
  telegram: 'teacheroman',
  whatsapp: '254714861813',
  email: 'romanvolkonidov@gmail.com',
  phone: '+254714861813',
};
const SITE_URL = 'https://rv2class.com';
const TELEGRAM_URL = `https://t.me/${CONTACT.telegram}`;
const WHATSAPP_URL = `https://wa.me/${CONTACT.whatsapp}`;
const EMAIL_URL = `mailto:${CONTACT.email}`;

/* ── Shared styles ────────────────────────────────────────────── */
const FOCUS = 'focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/30';
const BTN = `inline-flex min-h-[48px] items-center justify-center gap-2 rounded-full px-6 text-base font-bold transition ${FOCUS}`;
const BTN_PRIMARY = `${BTN} bg-brand-600 text-white hover:bg-brand-700`;
const BTN_SECONDARY = `${BTN} border border-slate-300 bg-white text-brand-ink hover:border-brand-500 hover:text-brand-600`;
const LINK = `inline-flex min-h-[44px] items-center gap-1.5 font-bold text-brand-600 underline decoration-brand-200 underline-offset-4 hover:decoration-brand-600 ${FOCUS}`;

/* ── Copy ─────────────────────────────────────────────────────── */
const T = {
  ru: {
    brand: 'Роман Волконидов',
    role: 'Английский для детей и подростков',
    nav: { lessons: 'Уроки', pricing: 'Цены', results: 'Результаты', course: 'Курс чтения', sheets: 'Шпаргалки', verbs: 'Тренажёр', about: 'Обо мне', faq: 'Вопросы', login: 'Войти', trial: 'Бесплатный урок', menuOpen: 'Открыть меню', menuClose: 'Закрыть меню', switchLang: 'Switch to English' },
    hero: {
      eyebrow: 'Английский онлайн · дети и подростки 5–17 лет',
      title: 'Ребёнок заговорит по-английски',
      titleAccent: 'и будет ждать следующего урока',
      subtitle: 'Живые уроки в Zoom с преподавателем, у которого сертификат TESOL и больше семи лет работы с детьми из России, Казахстана и Европы. Индивидуально, в паре или в небольшой группе — формат подберём под ребёнка и ваш бюджет.',
      ctaPrimary: 'Записаться на бесплатный урок',
      ctaSecondary: 'Как проходят уроки',
      trialNote: 'Пробный урок — 20 минут в Zoom: познакомимся, определю уровень, отвечу на вопросы. Ни к чему не обязывает.',
      photoAlt: 'Роман Волконидов, преподаватель английского',
      photoCaption: 'Кисуму, Кения · уроки онлайн',
      stats: [['TESOL', 'сертификат, Канада'], ['7+', 'лет преподаю детям'], ['170+', 'учеников из разных стран']],
    },
    outcomes: {
      title: 'Что изменится у ребёнка',
      lead: 'Родители приходят с разными задачами: заговорить, догнать класс, подготовиться к школе за рубежом. Программу строю вокруг этой задачи.',
      items: [
        { title: 'Говорит с первого урока', text: 'Большая часть урока — разговор: ребёнок отвечает, спрашивает, пересказывает, спорит. Грамматику и слова разбираем по ходу, на том же материале.' },
        { title: 'Успевает в школе', text: 'Готовлю к учёбе в англоязычной школе, к тестам и экзаменам, помогаю разобраться со школьными заданиями — чтобы ребёнок понимал, что происходит на уроке, и чувствовал себя уверенно.' },
        { title: 'Занимается по своему плану', text: 'После пробного урока составляю программу под уровень, цель и характер ребёнка и корректирую её по ходу. Это касается и детей, которым учёба даётся труднее.' },
        { title: 'Вы знаете, как идут дела', text: 'Регулярно рассказываю о результатах. Домашние задания короткие — в боте и на онлайн-платформе — и закрепляют то, что разобрали на уроке.' },
      ],
    },
    lessons: {
      title: 'Как проходят уроки',
      steps: [
        { title: 'Бесплатный пробный урок', text: '20 минут в Zoom. Знакомимся, я смотрю, что ребёнок уже знает, вы задаёте вопросы.' },
        { title: 'План и расписание', text: 'Предлагаю формат, программу и частоту занятий — обычно 2–3 урока в неделю. Время подбираем под вашу семью.' },
        { title: 'Регулярные уроки', text: '45 минут в Zoom, между уроками — короткая домашка в боте. Перенос бесплатный, если предупредить за 24 часа.' },
      ],
    },
    pricing: {
      title: 'Форматы и цены',
      lead: 'Все уроки — 45 минут, материалы включены. Цена в долларах; рубли — примерный эквивалент по текущему курсу.',
      badge: 'Выгоднее всего',
      plans: [
        { name: 'В группе', price: '$8–10', per: 'с человека за урок', desc: 'Группа 3–4 человека — $10 с человека, 5–6 человек — $8. Ребёнок говорит и с учителем, и с ровесниками.', cta: 'Записаться в группу', highlight: true },
        { name: 'В паре', price: '$15', per: 'с человека за урок', desc: 'Вдвоём с другом, братом или сестрой. Гибкий график.', cta: 'Записаться', highlight: false },
        { name: 'Индивидуально', price: '$25', per: 'за урок', desc: 'Программа только под вашего ребёнка, поддержка в чате между уроками, приоритет в расписании.', cta: 'Записаться', highlight: false },
      ],
      trial: 'Пробный урок — бесплатно. 20 минут: знакомство и определение уровня.',
      notes: [
        'Точная сумма в рублях рассчитывается по курсу на день оплаты.',
        'Оплата помесячно, вперёд — картой МИР, Visa, Mastercard или другим удобным способом.',
        'Перенос урока бесплатный при предупреждении за 24 часа.',
      ],
    },
    results: {
      title: 'Истории родителей',
      lead: 'Как это выглядит на практике — словами родителей.',
      cases: [
        { name: 'Наталья', role: 'мама Ульяны, 10 лет', location: 'Испания', quote: 'У дочки огромный прорыв. Теперь она понимает почти всё на уроках в школе и сдала экзамен на 8,2 из 10 — потрясающий результат!', meta: 'Начинали с нуля. Через 4 месяца, 3 урока в неделю.' },
        { name: 'Гарик', role: 'папа Сары, 11 лет, и Мэри, 8 лет', location: 'Испания', quote: 'Девочки теперь лидеры по английскому в классе — даже секретничают на нём между собой! Сара сдала итоговый тест на 10 из 10, лучше девочки из английской академии.', meta: 'Были слабейшими в классе. Через 6 месяцев, 4–5 уроков в неделю.' },
      ],
      reviews: 'Отзывы учеников и родителей',
      student: 'Ученик',
    },
    course: {
      eyebrow: 'Курс чтения · 5–9 лет',
      title: 'Read with Me',
      text: 'От первых звуков к коротким рассказам: 12 недель, по 15 минут в день с родителем. Всё озвучено.',
      priceNote: 'На год · без автопродления',
      buy: 'Купить курс',
      login: 'Уже купили? Войти',
      usdNote: 'цена в долларах, в других валютах — примерно',
    },
    sheets: {
      eyebrow: 'Бесплатно · для родителей',
      title: '10 шпаргалок по правилам чтения',
      text: 'Правило, слова-примеры и транскрипция на одном листе. Нажмите на слово — оно прозвучит; любой лист можно распечатать.',
      cta: 'Открыть бесплатно',
    },
    about: {
      eyebrow: 'Кто ведёт уроки',
      title: 'Роман Волконидов',
      paras: [
        'Преподаю английский детям больше семи лет; за это время у меня занимались больше 170 учеников из России, Казахстана и Европы. У меня сертификат TESOL (Канада).',
        'Живу в Кисуму, в Кении, где английский — язык школы, работы и улицы, поэтому на уроках ребёнок слышит живую речь, а не только учебник.',
        'Занимаюсь и с детьми, которым учёба даётся труднее: темп, материал и форму заданий подстраиваю под каждого.',
      ],
      facts: ['Сертификат TESOL (Канада)', 'Больше 7 лет преподавания', 'Больше 170 учеников', 'Уроки в Zoom, 45 минут', 'Русский и английский'],
      photoAlt: 'Роман Волконидов',
    },
    faq: {
      title: 'Частые вопросы',
      items: [
        { q: 'С какого возраста вы занимаетесь?', a: 'С 5 до 17 лет, от нуля до продвинутого уровня. Со взрослыми тоже занимаюсь — напишите, обсудим задачу.' },
        { q: 'Сколько длится урок и как часто заниматься?', a: 'Урок — 45 минут. Для заметного прогресса советую 2–3 урока в неделю, график подберём под вас.' },
        { q: 'Что нужно для занятий?', a: 'Ноутбук или планшет с камерой и интернет. Уроки проходят в Zoom.' },
        { q: 'Есть ли домашние задания?', a: 'Да, короткие: в боте и на онлайн-платформе. Они закрепляют то, что разобрали на уроке.' },
        { q: 'Как я узнаю, что есть прогресс?', a: 'По результатам заданий, по моей регулярной обратной связи и по тому, как ребёнок начинает говорить.' },
        { q: 'Как оплачивать?', a: 'Помесячно, вперёд — картой МИР, Visa, Mastercard или другим удобным способом. Цена в долларах, сумма в рублях считается по курсу на день оплаты.' },
        { q: 'Что, если мы не сможем прийти на урок?', a: 'Перенос бесплатный, если предупредить минимум за 24 часа.' },
      ],
    },
    contact: {
      title: 'Запишитесь на бесплатный урок',
      lead: 'Напишите мне в Telegram: возраст ребёнка и что вы хотите от английского. Отвечу и предложу время для пробного урока.',
      prefill: 'Здравствуйте! Хочу записаться на бесплатный пробный урок.',
      telegram: 'Написать в Telegram',
      telegramNote: 'Отвечаю быстрее всего здесь',
      whatsapp: 'WhatsApp',
      email: 'Почта',
    },
    footer: { tagline: 'Онлайн-уроки английского для детей и подростков. Zoom, 45 минут, первый урок бесплатно.', quick: 'Разделы', legal: 'Документы', contactL: 'Контакты', terms: 'Условия использования', privacy: 'Политика конфиденциальности', refund: 'Политика возврата', offer: 'Публичная оферта', rights: 'Все права защищены.', requisites: 'Волконидов Роман Андреевич · ИНН 263606300019' },
  },
  en: {
    brand: 'Roman Volkonidov',
    role: 'English for children and teenagers',
    nav: { lessons: 'Lessons', pricing: 'Prices', results: 'Results', course: 'Reading course', sheets: 'Cheat sheets', verbs: 'Verb trainer', about: 'About', faq: 'FAQ', login: 'Log in', trial: 'Free trial', menuOpen: 'Open menu', menuClose: 'Close menu', switchLang: 'Переключить на русский' },
    hero: {
      eyebrow: 'Online English · children and teenagers 5–17',
      title: 'Your child will speak English',
      titleAccent: 'and look forward to the next lesson',
      subtitle: 'Live lessons on Zoom with a TESOL-certified teacher who has spent more than seven years teaching children from Russia, Kazakhstan and Europe. One-to-one, in a pair or in a small group — we choose the format that suits your child and your budget.',
      ctaPrimary: 'Book a free trial lesson',
      ctaSecondary: 'How lessons work',
      trialNote: 'The trial is 20 minutes on Zoom: we meet, I assess the level and answer your questions. No commitment.',
      photoAlt: 'Roman Volkonidov, English teacher',
      photoCaption: 'Kisumu, Kenya · teaching online',
      stats: [['TESOL', 'certified, Canada'], ['7+', 'years teaching children'], ['170+', 'students from many countries']],
    },
    outcomes: {
      title: 'What changes for your child',
      lead: 'Parents come with different goals: to start speaking, to catch up with the class, to get ready for a school abroad. I build the programme around that goal.',
      items: [
        { title: 'Speaks from the first lesson', text: 'Most of the lesson is conversation: your child answers, asks, retells and argues. Grammar and vocabulary are covered on the way, using the same material.' },
        { title: 'Keeps up at school', text: 'I prepare children for an English-speaking school, for tests and exams, and help with school assignments — so your child understands what is happening in class and feels confident.' },
        { title: 'Follows their own plan', text: 'After the trial I write a programme around your child’s level, goal and personality and adjust it as we go. That includes children who find learning harder.' },
        { title: 'You know how it is going', text: 'I report on progress regularly. Homework is short — in a bot and on the online platform — and reinforces what we covered in the lesson.' },
      ],
    },
    lessons: {
      title: 'How lessons work',
      steps: [
        { title: 'Free trial lesson', text: '20 minutes on Zoom. We meet, I see what your child already knows, you ask your questions.' },
        { title: 'Plan and schedule', text: 'I suggest a format, a programme and a frequency — usually 2–3 lessons a week. We pick times that work for your family.' },
        { title: 'Regular lessons', text: '45 minutes on Zoom, with short homework in a bot between lessons. Rescheduling is free with 24 hours’ notice.' },
      ],
    },
    pricing: {
      title: 'Formats and prices',
      lead: 'Every lesson is 45 minutes, materials included. Prices are in USD; the rouble figure is an approximate equivalent at the current rate.',
      badge: 'Best value',
      plans: [
        { name: 'In a group', price: '$8–10', per: 'per person, per lesson', desc: 'Group of 3–4 — $10 per person, group of 5–6 — $8. Your child talks with the teacher and with peers.', cta: 'Join a group', highlight: true },
        { name: 'In a pair', price: '$15', per: 'per person, per lesson', desc: 'With a friend, brother or sister. Flexible schedule.', cta: 'Book', highlight: false },
        { name: 'One-to-one', price: '$25', per: 'per lesson', desc: 'A programme built only for your child, chat support between lessons, priority scheduling.', cta: 'Book', highlight: false },
      ],
      trial: 'Trial lesson — free. 20 minutes: we meet and I assess the level.',
      notes: [
        'The exact rouble amount is calculated at the exchange rate on the payment date.',
        'Payment is monthly, in advance — by MIR, Visa or Mastercard card, or another convenient method.',
        'Rescheduling is free with at least 24 hours’ notice.',
      ],
    },
    results: {
      title: 'Parents’ stories',
      lead: 'What it looks like in practice, in parents’ own words.',
      cases: [
        { name: 'Natalia', role: 'mother of Ulyana, 10', location: 'Spain', quote: 'My daughter has had a huge breakthrough. She now understands almost everything in class and scored 8.2 out of 10 on her school exam — an amazing result!', meta: 'Started from zero. After 4 months, 3 lessons a week.' },
        { name: 'Garik', role: 'father of Sara, 11, and Mary, 8', location: 'Spain', quote: 'The girls are now the leaders in English in their class — they even speak it secretly to each other! Sara scored 10 out of 10 on her final test, better than a girl who attends an English academy.', meta: 'Were the weakest in class. After 6 months, 4–5 lessons a week.' },
      ],
      reviews: 'Reviews from students and parents',
      student: 'Student',
    },
    course: {
      eyebrow: 'Reading course · ages 5–9',
      title: 'Read with Me',
      text: 'From first sounds to short stories: 12 weeks, 15 minutes a day with a parent. Everything is voiced.',
      priceNote: 'For a year · no auto-renewal',
      buy: 'Buy the course',
      login: 'Already bought it? Log in',
      usdNote: 'price in USD, other currencies are approximate',
    },
    sheets: {
      eyebrow: 'Free · for parents',
      title: '10 reading-rule cheat sheets',
      text: 'A rule, example words and transcription on one page. Tap a word to hear it; print any sheet.',
      cta: 'Open for free',
    },
    about: {
      eyebrow: 'Who teaches',
      title: 'Roman Volkonidov',
      paras: [
        'I have been teaching English to children for more than seven years; in that time more than 170 students from Russia, Kazakhstan and Europe have studied with me. I hold a TESOL certificate (Canada).',
        'I live in Kisumu, Kenya, where English is the language of school, work and the street, so in lessons your child hears living speech, not just the textbook.',
        'I also teach children who find learning harder: I adapt the pace, the material and the form of the tasks to each one.',
      ],
      facts: ['TESOL certified (Canada)', 'More than 7 years of teaching', 'More than 170 students', 'Lessons on Zoom, 45 minutes', 'Russian and English'],
      photoAlt: 'Roman Volkonidov',
    },
    faq: {
      title: 'Frequently asked questions',
      items: [
        { q: 'What ages do you teach?', a: 'From 5 to 17, from complete beginners to advanced. I also teach adults — write to me and we’ll discuss your goal.' },
        { q: 'How long is a lesson, and how often should we study?', a: 'A lesson is 45 minutes. For visible progress I recommend 2–3 lessons a week; we’ll choose a schedule that suits you.' },
        { q: 'What do we need?', a: 'A laptop or tablet with a camera and an internet connection. Lessons are on Zoom.' },
        { q: 'Is there homework?', a: 'Yes, short homework in a bot and on the online platform. It reinforces what we covered in the lesson.' },
        { q: 'How will I know there is progress?', a: 'From homework results, from my regular feedback and from the way your child starts to speak.' },
        { q: 'How do we pay?', a: 'Monthly, in advance — by MIR, Visa or Mastercard card, or another convenient method. Prices are in USD; the rouble amount is calculated at the rate on the payment date.' },
        { q: 'What if we can’t make a lesson?', a: 'Rescheduling is free with at least 24 hours’ notice.' },
      ],
    },
    contact: {
      title: 'Book a free trial lesson',
      lead: 'Message me on Telegram with your child’s age and what you want from English. I’ll reply and suggest a time for the trial.',
      prefill: 'Hello! I’d like to book a free trial lesson.',
      telegram: 'Message on Telegram',
      telegramNote: 'Fastest reply here',
      whatsapp: 'WhatsApp',
      email: 'Email',
    },
    footer: { tagline: 'Online English lessons for children and teenagers. Zoom, 45 minutes, first lesson free.', quick: 'Sections', legal: 'Legal', contactL: 'Contact', terms: 'Terms of Service', privacy: 'Privacy Policy', refund: 'Refund Policy', offer: 'Public Offer', rights: 'All rights reserved.', requisites: 'Волконидов Роман Андреевич · ИНН 263606300019' },
  },
};

const SEO = {
  ru: {
    title: 'Роман Волконидов — английский онлайн для детей и подростков. Первый урок бесплатно',
    description: 'Онлайн-уроки английского для детей 5–17 лет: индивидуально, в паре и в группе, 45 минут в Zoom. Преподаватель с сертификатом TESOL, 7+ лет опыта, 170+ учеников. Курс чтения Read with me для детей 5–9 лет. Пробный урок бесплатно.',
    ogTitle: 'Роман Волконидов — английский для детей онлайн',
    ogDescription: 'Уроки в Zoom с преподавателем TESOL. Индивидуально, в паре, в группе. Первый урок бесплатно.',
    locale: 'ru_RU',
  },
  en: {
    title: 'Roman Volkonidov — online English for children and teenagers. First lesson free',
    description: 'Online English lessons for children aged 5–17: one-to-one, in pairs and in groups, 45 minutes on Zoom. TESOL-certified teacher, 7+ years of experience, 170+ students. Read with me reading course for ages 5–9. Free trial lesson.',
    ogTitle: 'Roman Volkonidov — online English for children',
    ogDescription: 'Zoom lessons with a TESOL-certified teacher. One-to-one, pairs, groups. First lesson free.',
    locale: 'en_US',
  },
};

/* ── Helpers ──────────────────────────────────────────────────── */
function SectionHeading({ eyebrow, title, lead, center = false }) {
  return (
    <div className={`mb-10 max-w-2xl md:mb-14 ${center ? 'mx-auto text-center' : ''}`}>
      {eyebrow && <p className="mb-3 text-sm font-bold uppercase tracking-[0.12em] text-brand-600">{eyebrow}</p>}
      <h2 className="text-3xl font-extrabold leading-tight tracking-tight text-brand-ink sm:text-4xl" style={{ textWrap: 'balance' }}>{title}</h2>
      {lead && <p className="mt-4 text-lg leading-relaxed text-brand-muted">{lead}</p>}
    </div>
  );
}

/* Подставляет рублёвый эквивалент после каждой суммы в долларах внутри текста. */
function PriceText({ text, rubFromUsd }) {
  const parts = text.split(/(\$\d+(?:[.,]\d+)?)/);
  return (
    <>
      {parts.map((part, i) => {
        if (!/^\$/.test(part)) return part;
        const rub = rubFromUsd(part);
        return <span key={i} className="font-bold text-brand-ink">{part}{rub ? ` (≈ ${rub})` : ''}</span>;
      })}
    </>
  );
}

function ContactLink({ href, Icon, label, sub }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={`flex min-h-[56px] items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 transition hover:border-brand-500 ${FOCUS}`}>
      <Icon className="h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block text-sm font-bold text-brand-ink">{label}</span>
        <span className="block truncate text-sm text-slate-500">{sub}</span>
      </span>
    </a>
  );
}

/* Фрагмент настоящего курса Read with me: вёрстка, а не картинка. */
function CoursePreview() {
  const sounds = ['s', 'a', 't', 'p', 'i', 'n'];
  const words = ['sat', 'pat', 'pan', 'tap', 'nap'];
  return (
    <div className="mx-auto w-full max-w-md rounded-[1.75rem] bg-white p-5 shadow-xl shadow-slate-900/10 ring-1 ring-slate-200" aria-hidden="true">
      <div className="flex items-center gap-2">
        <svg className="h-8 w-8" viewBox="0 0 48 48" fill="none">
          <rect x="3" y="7" width="42" height="34" rx="9" fill="#FFD43B" />
          <path d="M24 13v22" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />
          <path d="M9 15h11a4 4 0 0 1 4 4v16a4 4 0 0 0-4-4H9z" fill="#fff" />
          <path d="M39 15H28a4 4 0 0 0-4 4v16a4 4 0 0 1 4-4h11z" fill="#fff" />
          <circle cx="15" cy="23" r="2.2" fill="#FF4D6D" /><circle cx="33" cy="23" r="2.2" fill="#3D7BFF" />
        </svg>
        <span className="text-xl font-extrabold text-[#22243A]">Read <span className="text-[#FF4D6D]">with</span> me</span>
        <span className="ml-auto rounded-full bg-[#3D7BFF] px-3 py-1 text-xs font-extrabold text-white">Week 1</span>
      </div>
      <div className="mt-4 rounded-2xl bg-[#E7F0FF] p-4">
        <p className="text-[0.7rem] font-extrabold uppercase tracking-[0.1em] text-[#3D7BFF]">Set 1</p>
        <div className="mt-2 grid grid-cols-6 gap-1.5">
          {sounds.map((snd) => (
            <span key={snd} className={`rounded-xl bg-white py-2 text-center text-xl font-extrabold shadow-sm ${snd === 'a' || snd === 'i' ? 'text-[#FF4D6D]' : 'text-[#3D7BFF]'}`}>{snd}</span>
          ))}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-[1fr_1.35fr] gap-3">
        <div className="rounded-2xl bg-[#FFF8EC] px-2 py-3 text-center">
          <p className="text-xl font-extrabold text-[#FF4D6D]">a</p>
          {words.map((w, i) => (
            <p key={w} className={`mx-1 mt-1 rounded-lg py-0.5 text-lg font-extrabold ${i === 1 ? 'bg-[#FFD43B] text-[#22243A]' : 'text-[#22243A]'}`}>{w}</p>
          ))}
        </div>
        <div className="flex flex-col justify-between rounded-2xl bg-[#FFF8EC] p-3">
          <p className="text-xs font-extrabold uppercase tracking-[0.08em] text-[#6B7192]">Text 1 · Nan and Pip</p>
          <div className="space-y-1 text-lg font-extrabold leading-snug text-[#22243A]">
            <p>Nan taps a pan.</p>
            <p className="rounded-lg bg-[#FFD43B] px-1.5">Tap, tap, tap!</p>
            <p>Pip naps.</p>
          </div>
          <p className="flex items-center gap-1.5 text-xs font-bold text-[#6B7192]"><svg className="h-2.5 w-2.5" viewBox="0 0 10 10" fill="currentColor"><path d="M1 1l8 4-8 4z" /></svg>Read aloud</p>
        </div>
      </div>
    </div>
  );
}

function Stars({ rating = 5 }) {
  return (
    <div className="flex gap-0.5 text-amber-500" role="img" aria-label={`${rating} / 5`}>
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className="h-4 w-4" fill={s <= rating ? 'currentColor' : 'none'} strokeWidth={1.75} aria-hidden="true" />
      ))}
    </div>
  );
}

/* ── Page ─────────────────────────────────────────────────────── */
function HomePage() {
  const [lang, setLang] = useState(() =>
    typeof navigator !== 'undefined' && navigator.language && navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en',
  );
  const [reviews, setReviews] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const t = T[lang];
  const seo = SEO[lang];
  const { rubFromUsd } = usePriceEquivalents();
  const price = usePrice();
  const trialUrl = `${TELEGRAM_URL}?text=${encodeURIComponent(t.contact.prefill)}`;

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'reviews'), orderBy('timestamp', 'desc')));
        setReviews(
          snap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .map((r) => ({ ...r, date: r.timestamp?.toDate ? r.timestamp.toDate().toLocaleDateString() : '' })),
        );
      } catch (err) {
        console.error('Error fetching reviews:', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  const topReviews = useMemo(
    () => reviews.filter((r) => (r.rating ?? 5) >= 4 && (r.text || '').trim().length > 0).slice(0, 6),
    [reviews],
  );

  const navLinks = [
    ['#lessons', t.nav.lessons],
    ['#pricing', t.nav.pricing],
    ['#results', t.nav.results],
    ['#course', t.nav.course],
    ['#sheets', t.nav.sheets],
    // Тренажёр глаголов — подарок в группе (Роман, 29.10): с сайта ссылки нет, страница /verbs открыта.
    ['#about', t.nav.about],
    ['#faq', t.nav.faq],
  ];
  const headerLinks = navLinks.filter(([href]) => ['#lessons', '#pricing', '#course', '#about'].includes(href));

  const NavItem = ({ href, label, className, onClick }) => (
    href.startsWith('/')
      ? <Link to={href} className={className} onClick={onClick}>{label}</Link>
      : <a href={href} className={className} onClick={onClick}>{label}</a>
  );

  const ldJson = {
    '@context': 'https://schema.org',
    '@type': 'EducationalOrganization',
    name: 'Roman Volkonidov — English for children',
    url: SITE_URL,
    description: seo.description,
    email: CONTACT.email,
    telephone: CONTACT.phone,
    sameAs: [TELEGRAM_URL, WHATSAPP_URL],
    areaServed: 'Online',
    makesOffer: [
      { '@type': 'Offer', name: lang === 'ru' ? 'Урок в группе' : 'Group lesson', price: '8-10', priceCurrency: 'USD' },
      { '@type': 'Offer', name: lang === 'ru' ? 'Урок в паре' : 'Pair lesson', price: '15', priceCurrency: 'USD' },
      { '@type': 'Offer', name: lang === 'ru' ? 'Индивидуальный урок' : 'One-to-one lesson', price: '25', priceCurrency: 'USD' },
      { '@type': 'Offer', name: 'Read with me', price: '5.90', priceCurrency: 'USD' },
    ],
  };

  return (
    <div className="min-h-screen bg-white text-brand-ink antialiased" style={{ fontFamily: "'Manrope', ui-sans-serif, system-ui, sans-serif" }}>
      <Helmet>
        <html lang={lang} />
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={SITE_URL} />
        <meta name="theme-color" content="#ffffff" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={SITE_URL} />
        <meta property="og:title" content={seo.ogTitle} />
        <meta property="og:description" content={seo.ogDescription} />
        <meta property="og:image" content={`${SITE_URL}${profilePic}`} />
        <meta property="og:locale" content={seo.locale} />
        <meta property="og:locale:alternate" content={lang === 'ru' ? 'en_US' : 'ru_RU'} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link href="https://fonts.googleapis.com/css2?family=Manrope:wght@500;600;700;800&display=swap" rel="stylesheet" />
        <style>{`@media (prefers-reduced-motion:no-preference){html{scroll-behavior:smooth}}section{scroll-margin-top:72px}html,body{overflow-x:hidden}`}</style>
        <script type="application/ld+json">{JSON.stringify(ldJson)}</script>
      </Helmet>

      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-3 focus:z-[60] focus:rounded-full focus:bg-brand-600 focus:px-4 focus:py-2 focus:text-white">
        {lang === 'ru' ? 'К содержанию' : 'Skip to content'}
      </a>

      {/* ── Header ── */}
      <header className="sticky top-0 z-50 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-2 px-3 sm:px-5">
          <a href="#top" className={`flex min-h-[44px] items-center gap-2.5 rounded-full ${FOCUS}`}>
            <img src={logo} alt="" className="h-9 w-9 rounded-full object-cover" />
            <span className="hidden text-base font-extrabold text-brand-ink sm:block">{t.brand}</span>
          </a>
          <nav className="hidden items-center gap-1 lg:flex" aria-label={lang === 'ru' ? 'Разделы' : 'Sections'}>
            {headerLinks.map(([href, label]) => (
              <NavItem key={href} href={href} label={label} className={`whitespace-nowrap rounded-full px-3 py-2.5 text-sm font-bold text-brand-muted transition hover:text-brand-600 ${FOCUS}`} />
            ))}
            <Link to="/login" className={`whitespace-nowrap rounded-full px-3 py-2.5 text-sm font-bold text-brand-muted transition hover:text-brand-600 ${FOCUS}`}>{t.nav.login}</Link>
          </nav>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <button
              type="button"
              onClick={() => setLang(lang === 'ru' ? 'en' : 'ru')}
              className={`flex h-11 items-center gap-1.5 rounded-full border border-slate-300 px-3 text-sm font-bold text-brand-ink transition hover:border-brand-500 ${FOCUS}`}
              aria-label={t.nav.switchLang}
            >
              <Globe className="h-4 w-4" aria-hidden="true" />
              {lang === 'ru' ? 'EN' : 'RU'}
            </button>
            <a href={trialUrl} target="_blank" rel="noopener noreferrer" className="inline-flex h-11 items-center whitespace-nowrap rounded-full bg-brand-600 px-4 text-sm font-bold text-white transition hover:bg-brand-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-500/30">{t.nav.trial}</a>
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className={`flex h-11 w-11 items-center justify-center rounded-full border border-slate-300 text-brand-ink transition hover:border-brand-500 lg:hidden ${FOCUS}`}
              aria-label={menuOpen ? t.nav.menuClose : t.nav.menuOpen}
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
            >
              {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav id="mobile-menu" className="border-t border-slate-200 bg-white lg:hidden" aria-label={lang === 'ru' ? 'Меню' : 'Menu'}>
            <div className="mx-auto max-w-6xl px-3 py-2">
              {navLinks.map(([href, label]) => (
                <NavItem key={href} href={href} label={label} onClick={() => setMenuOpen(false)} className={`block rounded-xl px-3 py-3 text-base font-bold text-brand-ink transition hover:bg-slate-50 ${FOCUS}`} />
              ))}
              <Link to="/login" onClick={() => setMenuOpen(false)} className={`mt-1 block border-t border-slate-200 px-3 py-3.5 text-base font-bold text-brand-600 ${FOCUS}`}>{t.nav.login}</Link>
            </div>
          </nav>
        )}
      </header>

      <main id="main">
        {/* ── Hero ── */}
        <section id="top" className="border-b border-slate-200 bg-white">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 pb-16 pt-10 md:grid-cols-[1.15fr_1fr] md:gap-14 md:pb-24 md:pt-20">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.12em] text-brand-600">{t.hero.eyebrow}</p>
              <h1 className="mt-4 text-[2.25rem] font-extrabold leading-[1.08] tracking-tight text-brand-ink sm:text-5xl lg:text-[3.5rem]" style={{ textWrap: 'balance' }}>
                {t.hero.title} <span className="text-brand-600">{t.hero.titleAccent}</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-brand-muted">{t.hero.subtitle}</p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                <a href={trialUrl} target="_blank" rel="noopener noreferrer" className={BTN_PRIMARY}>
                  <Send className="h-5 w-5" aria-hidden="true" />{t.hero.ctaPrimary}
                </a>
                <a href="#lessons" className={BTN_SECONDARY}>{t.hero.ctaSecondary}</a>
              </div>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-slate-500">{t.hero.trialNote}</p>
              <dl className="mt-10 grid max-w-lg grid-cols-3 gap-4 border-t border-slate-200 pt-6">
                {t.hero.stats.map(([n, l]) => (
                  <div key={n}>
                    <dt className="text-3xl font-extrabold tracking-tight text-brand-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>{n}</dt>
                    <dd className="mt-1 text-sm font-semibold leading-snug text-slate-500">{l}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <figure className="mx-auto w-full max-w-[20rem] md:max-w-[24rem]">
              <img src={profilePic} alt={t.hero.photoAlt} width="793" height="788" className="aspect-square w-full rounded-[2rem] object-cover shadow-xl shadow-slate-900/10" />
              <figcaption className="mt-3 text-center text-sm font-semibold text-slate-500">{t.hero.photoCaption}</figcaption>
            </figure>
          </div>
        </section>

        {/* ── Outcomes ── */}
        <section id="outcomes" className="bg-white">
          <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 md:grid-cols-[1fr_1.5fr] md:gap-14 md:py-24">
            <SectionHeading title={t.outcomes.title} lead={t.outcomes.lead} />
            <ul className="divide-y divide-slate-200 border-y border-slate-200">
              {t.outcomes.items.map((it, i) => (
                <li key={it.title} className="grid gap-2 py-6 sm:grid-cols-[3rem_1fr] sm:gap-6">
                  <span className="text-sm font-extrabold text-brand-600" style={{ fontVariantNumeric: 'tabular-nums' }}>0{i + 1}</span>
                  <div>
                    <h3 className="text-lg font-extrabold text-brand-ink">{it.title}</h3>
                    <p className="mt-2 leading-relaxed text-brand-muted">{it.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── How lessons work ── */}
        <section id="lessons" className="border-y border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
            <SectionHeading title={t.lessons.title} />
            <ol className="grid gap-8 md:grid-cols-3 md:gap-10">
              {t.lessons.steps.map((st, i) => (
                <li key={st.title}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-600 text-lg font-extrabold text-white">{i + 1}</span>
                  <h3 className="mt-5 text-xl font-extrabold text-brand-ink">{st.title}</h3>
                  <p className="mt-2 leading-relaxed text-brand-muted">{st.text}</p>
                </li>
              ))}
            </ol>
            <div className="mt-12">
              <a href={trialUrl} target="_blank" rel="noopener noreferrer" className={BTN_PRIMARY}>
                <Send className="h-5 w-5" aria-hidden="true" />{t.hero.ctaPrimary}
              </a>
            </div>
          </div>
        </section>

        {/* ── Pricing ── */}
        <section id="pricing" className="bg-white">
          <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
            <SectionHeading title={t.pricing.title} lead={t.pricing.lead} />
            <ul className="divide-y divide-slate-200 border-y border-slate-200">
              {t.pricing.plans.map((pl) => {
                const rub = rubFromUsd(pl.price);
                return (
                  <li key={pl.name} className={`grid gap-4 py-7 md:grid-cols-[11rem_1fr_auto] md:items-center md:gap-8 ${pl.highlight ? 'md:-mx-6 md:rounded-3xl md:border-0 md:bg-slate-50 md:px-6' : ''}`}>
                    <div>
                      {pl.highlight && <span className="mb-2 inline-block rounded-full bg-brand-600 px-3 py-1 text-xs font-extrabold text-white">{t.pricing.badge}</span>}
                      <h3 className="text-xl font-extrabold text-brand-ink">{pl.name}</h3>
                    </div>
                    <div>
                      <p className="text-3xl font-extrabold tracking-tight text-brand-ink" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {pl.price}{rub && <span className="ml-2 text-lg font-bold text-slate-500">≈ {rub}</span>}
                      </p>
                      <p className="text-sm font-semibold text-slate-500">{pl.per}</p>
                      <p className="mt-2 max-w-xl leading-relaxed text-brand-muted"><PriceText text={pl.desc} rubFromUsd={rubFromUsd} /></p>
                    </div>
                    <a href={trialUrl} target="_blank" rel="noopener noreferrer" className={pl.highlight ? BTN_PRIMARY : BTN_SECONDARY}>{pl.cta}</a>
                  </li>
                );
              })}
            </ul>
            <p className="mt-6 flex items-start gap-2 font-bold text-brand-ink"><Check className="mt-1 h-5 w-5 shrink-0 text-brand-600" aria-hidden="true" />{t.pricing.trial}</p>
            <ul className="mt-6 max-w-2xl space-y-1.5 text-sm text-slate-500">
              {t.pricing.notes.map((n) => <li key={n}>{n}</li>)}
            </ul>
          </div>
        </section>

        {/* ── Results ── */}
        <section id="results" className="border-y border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
            <SectionHeading title={t.results.title} lead={t.results.lead} />
            <div className="grid gap-6 md:grid-cols-2">
              {t.results.cases.map((c) => (
                <figure key={c.name} className="flex flex-col rounded-3xl bg-white p-7 ring-1 ring-slate-200 sm:p-8">
                  <blockquote className="flex-1 text-lg leading-relaxed text-brand-ink">«{c.quote}»</blockquote>
                  <figcaption className="mt-6 border-t border-slate-200 pt-4">
                    <p className="font-extrabold text-brand-ink">{c.name} <span className="font-semibold text-slate-500">· {c.location}</span></p>
                    <p className="text-sm text-slate-500">{c.role}</p>
                    <p className="mt-2 text-sm font-bold text-brand-600">{c.meta}</p>
                  </figcaption>
                </figure>
              ))}
            </div>
            {topReviews.length > 0 && (
              <>
                <h3 className="mb-6 mt-16 text-2xl font-extrabold text-brand-ink">{t.results.reviews}</h3>
                <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {topReviews.map((r) => (
                    <li key={r.id} className="rounded-3xl bg-white p-6 ring-1 ring-slate-200">
                      <Stars rating={r.rating ?? 5} />
                      <p className="mt-3 leading-relaxed text-brand-muted">{r.text}</p>
                      <p className="mt-4 text-sm font-extrabold text-brand-ink">{r.name || t.results.student} {r.date && <span className="font-semibold text-slate-500">· {r.date}</span>}</p>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>

        {/* ── Reading course ── */}
        <section id="course" className="bg-white">
          <div className="mx-auto grid max-w-6xl items-center gap-12 px-5 py-16 md:grid-cols-2 md:py-24">
            <div>
              <SectionHeading eyebrow={t.course.eyebrow} title={t.course.title} lead={t.course.text} />
              <div className="mt-8 rounded-3xl bg-slate-50 p-6 ring-1 ring-slate-200">
                <p className="text-sm font-semibold text-slate-500">{t.course.priceNote}</p>
                <Link to="/account?buy=reading" className={`${BTN_PRIMARY} mt-4 w-full`}>{t.course.buy} — {price.usd(COURSE_USD)}</Link>
                {price.local(COURSE_USD) && <p className="mt-2 text-center text-xs text-slate-400">{price.local(COURSE_USD)} · {t.course.usdNote}</p>}
                <Link to="/account" className={`${LINK} mt-3 w-full justify-center`}>{t.course.login}</Link>
              </div>
            </div>
            <CoursePreview />
          </div>
        </section>

        {/* ── Free reading-rule sheets ── */}
        <section id="sheets" className="border-t border-slate-200 bg-[#FAFAF7]">
          <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 py-16 md:grid-cols-2 md:py-24">
            <Link to="/sheets" className="group relative mx-auto block h-[300px] w-full max-w-[300px] focus:outline-none md:order-first md:h-[430px] md:max-w-sm" aria-label={t.sheets.cta}>
              <img src="/sheet-images/u.webp?v=2" alt="" loading="lazy" className="absolute right-[2%] top-0 w-[62%] rotate-[8deg] rounded-xl bg-white shadow-xl ring-1 ring-slate-200 transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:translate-x-6 group-hover:-translate-y-2 group-hover:rotate-[14deg] group-focus-visible:translate-x-6 group-focus-visible:rotate-[14deg]" />
              <img src="/sheet-images/g.webp?v=2" alt="" loading="lazy" className="absolute left-[19%] top-4 w-[62%] rotate-[1deg] rounded-xl bg-white shadow-xl ring-1 ring-slate-200 transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:-translate-y-4 group-hover:rotate-[3deg] group-focus-visible:-translate-y-4" />
              <img src="/sheet-images/a.webp?v=2" alt="" loading="lazy" className="absolute left-[2%] top-8 w-[62%] -rotate-[6deg] rounded-xl bg-white shadow-2xl ring-1 ring-slate-200 transition-transform duration-500 ease-out motion-reduce:transition-none group-hover:-translate-x-6 group-hover:-rotate-[11deg] group-focus-visible:-translate-x-6 group-focus-visible:-rotate-[11deg]" />
            </Link>
            <div>
              <SectionHeading eyebrow={t.sheets.eyebrow} title={t.sheets.title} lead={t.sheets.text} />
              <Link to="/sheets" className={`${BTN_PRIMARY} mt-2`}>{t.sheets.cta}</Link>
            </div>
          </div>
        </section>

        {/* ── About ── */}
        <section id="about" className="border-y border-slate-200 bg-slate-50">
          <div className="mx-auto grid max-w-6xl items-start gap-10 px-5 py-16 md:grid-cols-[1fr_1.6fr] md:gap-14 md:py-24">
            <img src={profilePic} alt={t.about.photoAlt} width="793" height="788" className="mx-auto aspect-square w-full max-w-[18rem] rounded-[2rem] object-cover md:max-w-none" />
            <div>
              <SectionHeading eyebrow={t.about.eyebrow} title={t.about.title} />
              {t.about.paras.map((para) => <p key={para} className="-mt-6 mb-5 text-lg leading-relaxed text-brand-muted first:-mt-8">{para}</p>)}
              <ul className="mt-8 flex flex-wrap gap-2">
                {t.about.facts.map((b) => (
                  <li key={b} className="rounded-full bg-white px-4 py-2 text-sm font-bold text-brand-ink ring-1 ring-slate-200">{b}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section id="faq" className="bg-white">
          <div className="mx-auto max-w-3xl px-5 py-16 md:py-24">
            <SectionHeading title={t.faq.title} />
            <div className="divide-y divide-slate-200 border-y border-slate-200">
              {t.faq.items.map((item) => (
                <details key={item.q} className="group">
                  <summary className={`flex min-h-[56px] cursor-pointer list-none items-center justify-between gap-6 py-4 text-base font-extrabold text-brand-ink ${FOCUS} [&::-webkit-details-marker]:hidden`}>
                    {item.q}
                    <svg className="h-5 w-5 shrink-0 text-brand-600 transition group-open:rotate-45" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M10 4v12M4 10h12" /></svg>
                  </summary>
                  <p className="pb-5 pr-10 leading-relaxed text-brand-muted">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ── Contact ── */}
        <section id="contact" className="border-t border-slate-200 bg-slate-50">
          <div className="mx-auto max-w-6xl px-5 py-16 md:py-24">
            <div className="grid gap-10 md:grid-cols-[1.4fr_1fr] md:gap-14">
              <div>
                <SectionHeading title={t.contact.title} lead={t.contact.lead} />
                <a href={trialUrl} target="_blank" rel="noopener noreferrer" className={`${BTN_PRIMARY} -mt-2 w-full sm:w-auto`}>
                  <Send className="h-5 w-5" aria-hidden="true" />{t.contact.telegram}
                </a>
                <p className="mt-3 text-sm font-semibold text-slate-500">{t.contact.telegramNote} · @{CONTACT.telegram}</p>
              </div>
              <div className="space-y-3 md:pt-2">
                <ContactLink href={WHATSAPP_URL} Icon={MessageCircle} label={t.contact.whatsapp} sub={CONTACT.phone} />
                <ContactLink href={EMAIL_URL} Icon={Mail} label={t.contact.email} sub={CONTACT.email} />
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* ── Footer ── */}
      <footer className="bg-brand-ink text-slate-300">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 py-14 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={logoMark} alt="" className="h-9 w-9 object-contain" />
              <span className="text-base font-extrabold text-white">{t.brand}</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-slate-400">{t.footer.tagline}</p>
          </div>
          <nav aria-label={t.footer.quick}>
            <h2 className="mb-3 text-xs font-extrabold uppercase tracking-[0.12em] text-slate-400">{t.footer.quick}</h2>
            <ul className="space-y-1 text-sm">
              {navLinks.map(([href, label]) => (
                <li key={href}><NavItem href={href} label={label} className="inline-flex min-h-[44px] items-center transition hover:text-white" /></li>
              ))}
            </ul>
          </nav>
          <div>
            <h2 className="mb-3 text-xs font-extrabold uppercase tracking-[0.12em] text-slate-400">{t.footer.contactL}</h2>
            <ul className="space-y-1 text-sm">
              <li><a href={TELEGRAM_URL} className="inline-flex min-h-[44px] items-center transition hover:text-white">Telegram · @{CONTACT.telegram}</a></li>
              <li><a href={WHATSAPP_URL} className="inline-flex min-h-[44px] items-center transition hover:text-white">WhatsApp · {CONTACT.phone}</a></li>
              <li><a href={EMAIL_URL} className="inline-flex min-h-[44px] items-center break-all transition hover:text-white">{CONTACT.email}</a></li>
            </ul>
          </div>
          <div>
            <h2 className="mb-3 text-xs font-extrabold uppercase tracking-[0.12em] text-slate-400">{t.footer.legal}</h2>
            <ul className="space-y-1 text-sm">
              <li><Link to="/terms-of-service" className="inline-flex min-h-[44px] items-center transition hover:text-white">{t.footer.terms}</Link></li>
              <li><Link to="/privacy-policy" className="inline-flex min-h-[44px] items-center transition hover:text-white">{t.footer.privacy}</Link></li>
              <li><Link to="/refund-policy" className="inline-flex min-h-[44px] items-center transition hover:text-white">{t.footer.refund}</Link></li>
              <li><Link to="/offer" className="inline-flex min-h-[44px] items-center transition hover:text-white">{t.footer.offer}</Link></li>
              <li><Link to="/login" className="inline-flex min-h-[44px] items-center transition hover:text-white">{t.nav.login}</Link></li>
            </ul>
          </div>
        </div>
        {/* Реквизиты продавца: платёжный сервис требует их на самом сайте. */}
        <div className="border-t border-slate-700 px-5 py-6 text-center text-sm text-slate-400">
          <p>© {new Date().getFullYear()} {t.brand}. {t.footer.rights}</p>
          <p className="mt-1">{t.footer.requisites}</p>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;